import React, { useCallback, useEffect, useMemo } from 'react'
import { Col, Container, Form, Row, Spinner } from 'react-bootstrap'
import { Controller, FormProvider, useForm, useFormContext } from 'react-hook-form'
import { getUserDisplayName } from '../../apps/professional-app/fa/common/aifeatures/utils'
import { getIntegrationDisplayName, getIntegrationServiceIdsForCategory, IntegrationCategory, IntegrationServiceId } from '../../common/integrations'
import { useLanguageOptions } from '../../common/languageUtils'
import {
  createAdditionalObjectsValidatorRegistry,
  type AdditionalObjectsValidator,
  useAdditionalObjectsContactLinking,
} from '../../common/useAdditionalObjectsContactLinking'
import { Button } from '../../components/Button/Button'
import { FormFeedback } from '../../components/Form/FormFeedback'
import { Modal } from '../../components/Modal/Modal'
import { getRoleDisplayName } from '../../components/Roles/utils'
import { SelectDropdown, SelectOption } from '../../components/SelectDropdown/SelectDropdown'
import { type User, type UserCreateUpdateIntegrations, type UserLanguage, UserOwnerType } from '../../models/User'
import { selectAccountId } from '../../store/auth/authSlice'
import { useGetExternalUserByIdQuery, useSaveExternalUserByIdMutation, useUpdateExternalUserRolesMutation } from '../../store/externalUser/externalUser'
import { useAppSelector } from '../../store/hooks'
import { useGetRolesQuery } from '../../store/role/role'
import { useGetAllUsersQuery, useGetMeQuery } from '../../store/user/user'
import { ContactDropdown } from '../MeetingSummary/CRM/ContactDropdown'
import { useContactLabel } from '../MeetingSummary/CRM/V2/hooks'
import { getUserName } from '../utils'
import { DEFAULT_CONTACT_ROLE_ID } from './AddContactModal'
import { DocumentFolderLinkingSection } from './DocumentFolderLinkingSection'

export interface EditContactModalProps {
  userId: string
  show: boolean
  onSave: () => void
  onCancel: () => void
}

type FormValues = {
  firstName: string
  lastName: string
  email: string
  phoneNumber: string
  role: string
  ownerId: string
  integrations: UserCreateUpdateIntegrations[]
  language: UserLanguage | null
}

export const EditContactModal: React.FC<EditContactModalProps> = ({ userId, show, onSave, onCancel }) => {
  const accountId = useAppSelector(selectAccountId)

  const defaultValues = {
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    role: '',
    ownerId: '',
    language: null,
    integrations: [],
  }
  const validatorRegistry = useMemo(() => createAdditionalObjectsValidatorRegistry(), [])
  const { run: runAdditionalObjectsValidators, register: registerAdditionalObjectsValidator } = validatorRegistry
  const [saveUserById, { isLoading: isUpdating, isSuccess }] = useSaveExternalUserByIdMutation()
  const [updateUserRoles, { isLoading: isRolesUpdating, isSuccess: updateRolesSuccess }] = useUpdateExternalUserRolesMutation()

  useEffect(() => {
    if (isSuccess && updateRolesSuccess) {
      onSave()
    }
  }, [isSuccess, updateRolesSuccess, onSave])

  const form = useForm<FormValues>({ defaultValues, mode: 'onChange' })
  const {
    handleSubmit,
    formState: { isSubmitting },
  } = form

  const handleSave = async (data: FormValues) => {
    const { isValid, integrations } = await runAdditionalObjectsValidators(data.integrations)
    if (!isValid) {
      return
    }

    saveUserById({
      accountId,
      userId,
      userData: {
        firstName: data.firstName,
        lastName: data.lastName,
        name: getUserName(data.firstName, data.lastName) || undefined,
        email: data.email,
        //phoneNumber: data.phoneNumber,
        referenceUserId: null,
        owner:
          !data.ownerId || data.ownerId === 'null'
            ? null
            : {
                ownerId: data.ownerId,
                ownerType: UserOwnerType.USER,
              },
        integrations,
        settings: {
          language: data.language || null,
        },
      },
    })
    updateUserRoles({
      accountId,
      userId: userId,
      roleIds: [data.role],
    })
  }

  const body = (
    <Container>
      <Row className='pb-3'>
        <Col>
          <h4 className='d-flex justify-content-center align-items-center'>Edit Contact</h4>
        </Col>
      </Row>
      <Row>
        <FormProvider {...form}>
          <EditContactModalForm userId={userId} registerAdditionalObjectsValidator={registerAdditionalObjectsValidator} />
        </FormProvider>
      </Row>
    </Container>
  )

  return (
    <Modal
      show={show}
      onHide={onCancel}
      header={<></>}
      body={body}
      showFooterActions={true}
      onFooterPrimaryAction={handleSubmit(handleSave)}
      footerPrimaryActionLoading={isUpdating || isRolesUpdating || isSubmitting}
      centered={true}
    />
  )
}

interface EditContactModalFormProps {
  userId: string
  registerAdditionalObjectsValidator: (validator: AdditionalObjectsValidator) => () => void
}

const EditContactModalForm: React.FC<EditContactModalFormProps> = ({ userId, registerAdditionalObjectsValidator }) => {
  const accountId = useAppSelector(selectAccountId)
  const { languageOptions } = useLanguageOptions()

  const { data: currentUser, isFetching: isFetchingCurrentUser } = useGetMeQuery({ accountId })
  const { data: userData, isFetching: isFetchingUserData } = useGetExternalUserByIdQuery(
    {
      accountId,
      userId,
    },
    { refetchOnMountOrArgChange: true },
  )
  const { data: roleData, isFetching: isFetchingRoleData } = useGetRolesQuery(
    { accountId, pageRequest: { page: 0, size: 500 }, externalOnly: true },
    { refetchOnMountOrArgChange: true },
  )
  const { data: users, isFetching: usersFetching } = useGetAllUsersQuery({
    accountId: accountId,
    identityTypes: ['idp'],
  })

  const userOptions: SelectOption[] = [
    { id: 'null', label: 'No one' },
    ...(users?.data.map((user) => {
      return { id: user.id, label: getUserDisplayName(user) }
    }) ?? []),
  ]

  const { control, setValue, watch } = useFormContext<FormValues>()
  const integrations = watch('integrations')

  useEffect(() => {
    if (!userData) {
      return
    }
    setValue('firstName', userData.data.firstName ?? '')
    setValue('lastName', userData.data.lastName ?? '')
    setValue('email', userData.data.email ?? '')
    setValue('integrations', userData.data.integrations ?? [])
    setValue('ownerId', userData.data.owner?.ownerId ?? '')
    setValue('language', userData.data.settings?.language ?? null)

    const contactRoleId = userData.data.roles[0]?.id
    const defaultRoleId = roleData?.data.find((r) => r.id === DEFAULT_CONTACT_ROLE_ID)?.id
    setValue('role', contactRoleId ?? defaultRoleId ?? '')
  }, [userData, roleData, setValue])

  const roleOptions =
    roleData?.data.map((role) => {
      return { id: role.id, label: getRoleDisplayName(role) }
    }) || []

  const integrationsList =
    currentUser?.integrations
      ?.filter(
        (e) =>
          e.type === 'crm' || e.type === 'planning' || e.type === 'portfolio' || e.type === 'tax' || e.type === 'estate_planning' || e.type === 'custodian',
      )
      .map(({ serviceId }) => {
        const existingIntegration = integrations.find((e) => e.serviceId === serviceId)
        if (!existingIntegration) {
          return { serviceId }
        } else {
          return { serviceId, externalId: existingIntegration.externalId }
        }
      }) ?? []

  const documentServiceIds = useMemo(() => getIntegrationServiceIdsForCategory(IntegrationCategory.DOCUMENT), [])
  const documentIntegrations = useMemo(
    () => currentUser?.integrations?.filter((integration) => documentServiceIds.includes(integration.serviceId)) ?? [],
    [currentUser?.integrations, documentServiceIds],
  )

  return (
    <Container className='d-flex flex-column gap-4 p-0'>
      {isFetchingUserData || isFetchingRoleData || usersFetching || isFetchingCurrentUser ? (
        <div className='d-flex align-items-center justify-content-center'>
          <Spinner />
        </div>
      ) : (
        <>
          <Row>
            <Col>
              <Controller
                name='email'
                control={control}
                rules={{
                  required: 'Email address is required.',
                  pattern: {
                    value: /\S+@\S+\.\S+/,
                    message: 'Entered value does not match email format',
                  },
                }}
                render={({ field, fieldState }) => (
                  <>
                    <Form.Label>Email address*</Form.Label>
                    <Form.Control id={field.name} type='email' placeholder='Enter email address…' {...field} isInvalid={!!fieldState.error} />
                    {fieldState.error && <FormFeedback>{fieldState.error.message}</FormFeedback>}
                  </>
                )}
              />
            </Col>
          </Row>
          <Row>
            <Col>
              <Controller
                name='firstName'
                control={control}
                render={({ field }) => (
                  <>
                    <Form.Label>First name</Form.Label>
                    <Form.Control id={field.name} type='text' placeholder='Enter first name…' {...field} />
                  </>
                )}
              />
            </Col>
          </Row>
          <Row>
            <Col>
              <Controller
                name='lastName'
                control={control}
                render={({ field }) => (
                  <>
                    <Form.Label>Last name</Form.Label>
                    <Form.Control id={field.name} type='text' placeholder='Enter last name…' {...field} />
                  </>
                )}
              />
            </Col>
          </Row>
          <Row>
            <Col>
              <Controller
                name='role'
                control={control}
                render={({ field, fieldState }) => (
                  <>
                    <Form.Label htmlFor={field.name}>Role</Form.Label>
                    <SelectDropdown
                      toggleId={field.name}
                      className='zocks-px-0.75 py-2'
                      options={roleOptions}
                      initialSelectedOption={roleOptions.find((option) => option.id === field.value)}
                      onOptionSelected={(option) => field.onChange(option.id)}
                    />
                    {fieldState.error && <FormFeedback>{fieldState.error.message}</FormFeedback>}
                  </>
                )}
              />
            </Col>
          </Row>
          <Row>
            <Col>
              <Controller
                name='ownerId'
                control={control}
                render={({ field, fieldState }) => (
                  <>
                    <Form.Label htmlFor={field.name}>Owner</Form.Label>
                    <SelectDropdown
                      toggleId={field.name}
                      className='zocks-px-0.75 py-2'
                      options={userOptions}
                      initialSelectedOption={userOptions.find((option) => option.id === field.value)}
                      onOptionSelected={(option) => {
                        field.onChange(option.id)
                      }}
                      disabled={usersFetching}
                    />
                    {fieldState.error && <FormFeedback>{fieldState.error.message}</FormFeedback>}
                  </>
                )}
              />
            </Col>
          </Row>
          <Row>
            <Col>
              <Controller
                name='language'
                control={control}
                render={({ field, fieldState }) => (
                  <>
                    <Form.Label htmlFor={field.name}>Language</Form.Label>
                    <SelectDropdown
                      toggleId={field.name}
                      className='zocks-px-0.75 py-2'
                      options={languageOptions}
                      initialSelectedOption={field.value ? (languageOptions.find((option) => option.id === field.value) ?? null) : null}
                      onOptionSelected={(option) => field.onChange(option.id as UserLanguage)}
                      placeholder='Select Language'
                    />
                    {fieldState.error && <FormFeedback>{fieldState.error.message}</FormFeedback>}
                  </>
                )}
              />
            </Col>
          </Row>
          {integrationsList.map(({ serviceId, externalId }) => (
            <IntegrationSelectorRow
              key={serviceId}
              serviceId={serviceId}
              externalId={externalId}
              registerAdditionalObjectsValidator={registerAdditionalObjectsValidator}
            />
          ))}
          {userData?.data
            ? documentIntegrations.map((integration) => (
                <DocumentFolderLinkingSection key={integration.serviceId} serviceId={integration.serviceId} contact={userData.data} />
              ))
            : null}
        </>
      )}
    </Container>
  )
}

interface IntegrationSelectorRowProps {
  serviceId: IntegrationServiceId
  externalId?: string
  registerAdditionalObjectsValidator: (validator: AdditionalObjectsValidator) => () => void
}

const IntegrationSelectorRow: React.FC<IntegrationSelectorRowProps> = ({ serviceId, externalId, registerAdditionalObjectsValidator }) => {
  const { getValues, setValue } = useFormContext<FormValues>()

  const { isAdditionalObjectsContactLinking, identifierValue, identifierError, isLoadingIdentifier, handleIdentifierChange } =
    useAdditionalObjectsContactLinking({
      serviceId,
      initialExternalId: externalId,
      registerAdditionalObjectsValidator,
    })

  const serviceIdDisplayName = getIntegrationDisplayName(serviceId)
  const { contactLabel } = useContactLabel(serviceId)

  const handleContactSelected = useCallback(
    (user: User) => {
      const contactIntegration = user.integrations?.find((i) => i.serviceId === serviceId)
      if (!contactIntegration) {
        return
      }

      const userIntegration: UserCreateUpdateIntegrations = {
        id: contactIntegration.id,
        type: contactIntegration.type,
        serviceId: contactIntegration.serviceId,
        externalId: contactIntegration.externalId,
        externalAccountId: contactIntegration.externalAccountId,
      }

      const currentIntegrations = getValues('integrations') ?? []
      const integrationIndex = currentIntegrations.findIndex((e) => e.serviceId === serviceId)
      const newIntegrations = [...currentIntegrations]
      if (integrationIndex > -1) {
        newIntegrations[integrationIndex] = userIntegration
      } else {
        newIntegrations.push(userIntegration)
      }
      setValue('integrations', newIntegrations)
    },
    [serviceId, getValues, setValue],
  )

  const handleRemoveButtonClicked = useCallback(() => {
    const currentIntegrations = getValues('integrations') ?? []
    setValue(
      'integrations',
      currentIntegrations.filter((e) => e.serviceId !== serviceId),
    )
    handleIdentifierChange('')
  }, [serviceId, getValues, setValue, handleIdentifierChange])

  return (
    <Row>
      <Col>
        <Form.Label>
          Linked {serviceIdDisplayName} {contactLabel}
        </Form.Label>

        {isAdditionalObjectsContactLinking && isLoadingIdentifier ? (
          <div className='d-flex justify-content-center p-2'>
            <Spinner />
          </div>
        ) : (
          <div className='d-flex align-items-center zocks-gap-0.5'>
            {isAdditionalObjectsContactLinking ? (
              <div className='flex-grow-1'>
                <Form.Control
                  type='text'
                  value={identifierValue}
                  placeholder='Enter external identifier…'
                  onChange={(e) => handleIdentifierChange(e.target.value)}
                  isInvalid={!!identifierError}
                />
              </div>
            ) : (
              <div className='flex-grow-1'>
                <ContactDropdown
                  serviceId={serviceId}
                  initialSelectedOptionExternalId={externalId}
                  onContactSelected={handleContactSelected}
                  showLabel={false}
                  prefetchContacts
                />
              </div>
            )}
            <Button variant='primaryGhost' className='zocks-p-0.75' onClick={handleRemoveButtonClicked} disabled={!externalId && !identifierValue}>
              <i className='bi bi-trash' />
            </Button>
          </div>
        )}
      </Col>
    </Row>
  )
}
